import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { fetchSupportAttachmentBlob } from '../api/endpoints';
import type { SupportAttachment, SupportMessage, SupportTicketListItem } from '../api/types';
import { formatShortDateTime } from '../utils/format';
import { SUPPORT_ATTACHMENT_LIMITS, validateSupportFiles } from '../utils/validation';
import { statusBorderClass } from './StatusBadge';

/*
 * Shared by the client's Support pages, the SuperAdmin Support Inbox and the Support tab on Client
 * detail. Rules and labels come from Core (SupportTicketService); FEATURES.md 8e.
 */

const STATUS_CLASS: Record<string, string> = {
  Open: 'badge-pending',
  InProgress: 'badge-inprogress',
  WaitingOnClient: 'badge-sev-info',
  Resolved: 'badge-approved',
  Closed: 'badge-expired',
};

const PRIORITY_CLASS: Record<string, string> = {
  Low: 'badge-low',
  Normal: 'badge-expired',
  High: 'badge-high',
  Urgent: 'badge-critical',
};

function Badge({ cls, children, title }: { cls: string; children: ReactNode; title?: string }) {
  return (
    <span className={`badge badge-status ${cls} border ${statusBorderClass(cls)}`} title={title}>
      {children}
    </span>
  );
}

export function SupportStatusBadge({ status, label }: { status: string; label: string }) {
  return <Badge cls={STATUS_CLASS[status] ?? 'bg-secondary'}>{label}</Badge>;
}

export function SupportPriorityBadge({ priority }: { priority: string }) {
  return <Badge cls={PRIORITY_CLASS[priority] ?? 'bg-secondary'}>{priority}</Badge>;
}

/** Labels where an inbox row came from: a client's portal request, or the landing page form. */
export function SupportSourceBadge({ source }: { source: string }) {
  return source === 'ContactForm' ? (
    <Badge cls="badge-sev-security" title="Sent from the website's Contact us form">
      <i className="bi bi-globe2 me-1" />
      Contact form
    </Badge>
  ) : (
    <Badge cls="badge-sev-info" title="Raised by a client from the portal">
      <i className="bi bi-building me-1" />
      Portal
    </Badge>
  );
}

function UnreadDot() {
  return (
    <span className="d-inline-block rounded-circle bg-primary me-2 flex-shrink-0" style={{ width: 8, height: 8 }}>
      <span className="visually-hidden">Unread reply</span>
    </span>
  );
}

/**
 * The request list. `team` adds the From and Assignee columns (the inbox); `linkBase` is where a
 * row opens (/support or /admin/support).
 */
export function SupportTicketTable({
  tickets,
  team,
  linkBase,
  hideClient = false,
  emptyText,
}: {
  tickets: SupportTicketListItem[];
  team: boolean;
  linkBase: string;
  /** On Client detail the client is already known. */
  hideClient?: boolean;
  emptyText: string;
}) {
  const columns = team ? (hideClient ? 7 : 8) : 7;
  return (
    <div className="table-responsive">
      <table className="table table-dashboard table-hover mb-0">
        <thead>
          <tr>
            <th style={{ width: '4.5rem' }}>#</th>
            <th>Subject</th>
            {team && !hideClient && <th>From</th>}
            {!team && <th>Raised by</th>}
            <th>Category</th>
            <th>Priority</th>
            <th>Status</th>
            {team && <th>Assignee</th>}
            <th>Last activity</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => (
            <tr key={t.id} className={t.unread ? 'fw-semibold' : undefined}>
              <td className="text-body-secondary">#{t.id}</td>
              <td style={{ maxWidth: '22rem' }}>
                <Link to={`${linkBase}/${t.id}`} className="text-decoration-none d-flex align-items-center">
                  {t.unread && <UnreadDot />}
                  <span className="text-truncate">{t.subject}</span>
                </Link>
              </td>
              {team && !hideClient && (
                <td>
                  {t.source === 'ContactForm' ? (
                    <div className="d-flex flex-column align-items-start gap-1">
                      <SupportSourceBadge source={t.source} />
                      <small className="text-body-secondary">
                        {t.contactName}
                        {t.companyName ? ` · ${t.companyName}` : ''}
                      </small>
                    </div>
                  ) : (
                    <div className="d-flex flex-column">
                      {t.clientId ? (
                        <Link to={`/clients/${t.clientId}?tab=support`} className="text-decoration-none">
                          {t.clientName}
                        </Link>
                      ) : (
                        <span>{t.clientName}</span>
                      )}
                      <small className="text-body-secondary">{t.contactName}</small>
                    </div>
                  )}
                </td>
              )}
              {!team && <td>{t.contactName}</td>}
              <td>
                <small>{t.categoryLabel}</small>
              </td>
              <td>
                <SupportPriorityBadge priority={t.priority} />
              </td>
              <td>
                <SupportStatusBadge status={t.status} label={t.statusLabel} />
              </td>
              {team && (
                <td>
                  <small className={t.assignedToName ? '' : 'text-body-secondary'}>{t.assignedToName ?? 'Unassigned'}</small>
                </td>
              )}
              <td className="text-body-secondary">
                <small>{formatShortDateTime(t.lastActivityAt)}</small>
              </td>
            </tr>
          ))}
          {tickets.length === 0 && (
            <tr>
              <td colSpan={columns} className="text-center text-body-secondary py-4">
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/** One message in the conversation. Internal notes are only ever present for the team. */
export function SupportMessageCard({ message }: { message: SupportMessage }) {
  const tone = message.isInternalNote
    ? 'border-warning-subtle bg-warning-subtle'
    : message.isStaff
      ? 'border-primary-subtle bg-primary-subtle'
      : '';
  return (
    <div className={`card mb-3 ${tone}`}>
      <div className="card-body py-3">
        <div className="d-flex justify-content-between align-items-center gap-2 mb-2 flex-wrap">
          <div className="fw-semibold small">
            <i className={`bi ${message.isStaff ? 'bi-headset' : 'bi-person-circle'} me-1`} />
            {message.authorName}
            {message.isInternalNote && (
              <span className="badge text-bg-warning ms-2">
                <i className="bi bi-lock-fill me-1" />
                Internal note
              </span>
            )}
          </div>
          <small className="text-body-secondary">{formatShortDateTime(message.createdAt)}</small>
        </div>
        <div className="small" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {message.body}
        </div>
        {message.attachments.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mt-3">
            {message.attachments.map((a) => (
              <AttachmentChip key={a.id} attachment={a} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * One attachment. The files are bearer-protected (like document images), so they are fetched
 * through the authenticated client: an image becomes a thumbnail that opens full size in a new
 * tab, anything else downloads under its own name.
 */
function AttachmentChip({ attachment: a }: { attachment: SupportAttachment }) {
  const [thumb, setThumb] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!a.isImage) return;
    let url: string | null = null;
    let cancelled = false;
    fetchSupportAttachmentBlob(a.id)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setThumb(url);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [a.id, a.isImage]);

  async function open() {
    if (thumb) {
      window.open(thumb, '_blank', 'noopener');
      return;
    }
    setBusy(true);
    try {
      const blob = await fetchSupportAttachmentBlob(a.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.fileName;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      className="btn btn-sm btn-light border d-flex align-items-center gap-2 text-start"
      style={{ maxWidth: '16rem' }}
      onClick={open}
      disabled={busy}
      title={failed ? 'This file could not be loaded' : `Open ${a.fileName}`}
    >
      {a.isImage && thumb ? (
        <img src={thumb} alt="" className="rounded" style={{ width: 48, height: 48, objectFit: 'cover' }} />
      ) : (
        <i className="bi bi-file-earmark-image fs-4" />
      )}
      <span className="d-flex flex-column" style={{ minWidth: 0 }}>
        <span className="text-truncate small fw-medium">{a.fileName}</span>
        <span className="text-body-secondary" style={{ fontSize: '0.75rem' }}>
          {failed ? 'Unavailable' : busy ? 'Downloading…' : formatFileSize(a.sizeBytes)}
        </span>
      </span>
    </button>
  );
}

/**
 * Choose up to three images to send with a message. Checked here for the obvious problems; the
 * server decodes and re-encodes each one and has the final word.
 */
export function AttachmentPicker({
  files,
  onChange,
  id,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  id: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const problem = validateSupportFiles(files);

  function add(list: FileList | null) {
    if (!list) return;
    onChange([...files, ...Array.from(list)]);
    if (input.current) input.current.value = '';
  }

  return (
    <div>
      <input
        ref={input}
        id={id}
        type="file"
        multiple
        className="d-none"
        accept={SUPPORT_ATTACHMENT_LIMITS.accept}
        onChange={(e) => add(e.target.files)}
      />
      <div className="d-flex flex-wrap align-items-center gap-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => input.current?.click()}
          disabled={files.length >= SUPPORT_ATTACHMENT_LIMITS.maxFiles}
        >
          <i className="bi bi-image me-1" />
          Attach images
        </button>
        {files.map((f, i) => (
          <span key={`${f.name}-${i}`} className="badge text-bg-light border d-inline-flex align-items-center gap-1 fw-normal">
            <i className="bi bi-file-earmark" />
            <span className="text-truncate" style={{ maxWidth: '12rem' }}>
              {f.name}
            </span>
            <span className="text-body-secondary">{formatFileSize(f.size)}</span>
            <button
              type="button"
              className="btn-close ms-1"
              style={{ fontSize: '0.55rem' }}
              aria-label={`Remove ${f.name}`}
              onClick={() => onChange(files.filter((_, j) => j !== i))}
            />
          </span>
        ))}
        {files.length === 0 && (
          <small className="text-body-secondary">
            Up to {SUPPORT_ATTACHMENT_LIMITS.maxFiles} screenshots or photos (PNG, JPEG, GIF, WebP),{' '}
            {SUPPORT_ATTACHMENT_LIMITS.maxTotalBytes / (1024 * 1024)} MB in total.
          </small>
        )}
      </div>
      {problem && <div className="text-danger small mt-1">{problem}</div>}
    </div>
  );
}
